import extractConfig from './utils/extractConfig';
import { PerformanceDatapoint } from './datapoints/PerformanceDatapoint';
import { PerformanceCoreDatapoint } from './datapoints/PerformanceCoreDatapoint';
import type { ActionMessage, DispatchFunction } from 'satcheljs';
import type RequestOptions from 'owa-service/lib/RequestOptions';
import type { CoreDatapointConfig, DatapointConfig } from 'owa-analytics-types';
import type { MailboxInfo } from 'owa-client-types';
import { lazyTrackNetworkResponse } from './lazyFunctions';
import type {
    DatapointConfigWithFunctions,
    CoreDatapointConfigWithFunctions,
} from './utils/extractConfig';
import { getActionMetadata } from './utils/getActionMetadata';
import type { AnalyticsCoreEventNames } from 'owa-analytics-events';
import { getAnalyticsAddon } from 'owa-analytics-addons';
import { addToTimingMap } from 'owa-performance/lib/utils/timingMap';

const MAX_ACTIONS_TO_KEEP = 7;

let actionDatapointStack: PerformanceDatapoint[] = [];
export function getActionDatapointStack(): PerformanceDatapoint[] {
    return actionDatapointStack;
}

export function clearActionDatapointStack(): void {
    actionDatapointStack = [];
}

export function returnTopExecutingActionDatapoint(
    filter: string | ((dp: PerformanceDatapoint) => boolean)
): PerformanceDatapoint | null | undefined {
    return actionDatapointStack.filter(
        typeof filter == 'function' ? filter : dp => dp.getEventName() == filter
    )[0];
}

function trackServiceResponses(
    responsePromise: Promise<Response>,
    actionName: string,
    _url: string,
    _attemptCount: number,
    optionsPromise: Promise<RequestOptions>
) {
    var datapoint = actionDatapointStack[0];
    if (datapoint) {
        lazyTrackNetworkResponse.importAndExecute(
            datapoint,
            responsePromise,
            actionName,
            optionsPromise
        );
    }
}

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function executeWhenReady called at module scope is not allowed. This may cause unwanted side effects.
 *	> Function getAnalyticsAddon called at module scope is not allowed. This may cause unwanted side effects. */
getAnalyticsAddon('RegisterCustomOwsCallbacks')?.executeWhenReady(trackServiceResponses);

export function addDatapointMiddleware(next: DispatchFunction, actionMessage: ActionMessage): any {
    const actionMetadata = getActionMetadata();
    actionMetadata.count++;
    const actionType = actionMessage.type;

    while (actionMetadata.queue.length >= MAX_ACTIONS_TO_KEEP) {
        actionMetadata.queue = actionMetadata.queue.slice(1);
    }
    if (actionType) {
        addToTimingMap('satchelAction', actionType);
        actionMetadata.queue.push(actionType);
        actionMetadata.actionMap.set(
            actionType,
            (actionMetadata.actionMap.get(actionType) ?? 0) + 1
        );
    }

    // if there is a datapoint config or action datapoint
    if (actionMessage.dp || actionMessage.actionDatapoint) {
        if (actionType) {
            actionMetadata.stack.push(actionType);
        }
        return addDataPointInternal(
            next.bind(null, actionMessage),
            actionMessage.dp,
            actionMessage
        );
    }

    return next(actionMessage);
}

export function wrapFunctionForDatapoint<
    DatapointArgs extends any[],
    WrappedFunctionArgs extends [...DatapointArgs, ...any[]],
    WrappedFunctionReturnType
>(
    config: DatapointConfigWithFunctions<DatapointArgs>,
    funcToWrap: (...args: WrappedFunctionArgs) => WrappedFunctionReturnType
): (...args: WrappedFunctionArgs) => WrappedFunctionReturnType {
    return wrapFunctionForDatapointInternal<
        DatapointArgs,
        WrappedFunctionArgs,
        WrappedFunctionReturnType
    >(config, funcToWrap, false /* isCore */);
}

export function wrapFunctionForCoreDatapoint<
    DatapointArgs extends any[],
    WrappedFunctionArgs extends [...DatapointArgs, ...any[]],
    WrappedFunctionReturnType
>(
    config: CoreDatapointConfigWithFunctions<DatapointArgs>,
    funcToWrap: (...args: WrappedFunctionArgs) => WrappedFunctionReturnType
): (...args: WrappedFunctionArgs) => WrappedFunctionReturnType {
    return wrapFunctionForDatapointInternal<
        DatapointArgs,
        WrappedFunctionArgs,
        WrappedFunctionReturnType
    >(config, funcToWrap, true /* isCore */);
}

function wrapFunctionForDatapointInternal<
    DatapointArgs extends any[],
    WrappedFunctionArgs extends [...DatapointArgs, ...any[]],
    WrappedFunctionReturnType
>(
    config: DatapointConfigWithFunctions<DatapointArgs>,
    funcToWrap: (...args: WrappedFunctionArgs) => WrappedFunctionReturnType,
    isCore: boolean
): (...args: WrappedFunctionArgs) => WrappedFunctionReturnType {
    return config
        ? (...args: WrappedFunctionArgs) => {
              return addDataPointInternal(
                  () => funcToWrap.apply(null, args),
                  extractConfig(config, args as unknown as DatapointArgs),
                  undefined /* actionMessage */,
                  isCore
              );
          }
        : funcToWrap;
}

function addDataPointInternal(
    executeNext: () => any,
    config: (DatapointConfig | CoreDatapointConfig) & {
        skipAutoEndDatapoint?: string;
        mailbox?: MailboxInfo;
    },
    actionMessage?: ActionMessage & {
        lazyOrchestrator?: boolean;
    },
    isCore?: boolean
) {
    let datapoint: PerformanceDatapoint | PerformanceCoreDatapoint | null = null;
    if (actionMessage?.actionDatapoint) {
        datapoint = actionMessage.actionDatapoint;
    } else if (config.name) {
        const options = config.options;
        // Prefer mailbox from config (for wrapFunctionForCoreDatapoint) over options.mailbox
        const mailbox = config.mailbox ?? options?.mailbox;

        datapoint =
            isCore && !(config as CoreDatapointConfig).disableIsCore
                ? new PerformanceCoreDatapoint(
                      config.name as AnalyticsCoreEventNames,
                      mailbox,
                      options
                  )
                : new PerformanceDatapoint(config.name, mailbox, options);
        // only push the datapoint on the stack if it has just been created
        actionDatapointStack.push(datapoint);

        if (datapoint) {
            try {
                if (config.customData) {
                    datapoint.addCustomData(config.customData);
                }
                if (config.cosmosOnlyData) {
                    datapoint.addCosmosOnlyData(config.cosmosOnlyData);
                }
                if (config.actionSource) {
                    datapoint.addActionSource(config.actionSource);
                }
                if (config.unifiedTelemetry) {
                    datapoint.addUnifiedTelemetryData(config.unifiedTelemetry);
                }
            } catch {
                // ignore errors when trying to add custom data
            }
        }
    }

    var returnValue: any = null;
    try {
        returnValue = executeNext();
    } catch (error) {
        if (datapoint) {
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'TraceErrorObject | undefined'.
            // @ts-expect-error
            datapoint.endAction(undefined, error);
            actionDatapointStack.pop();
        }

        throw error;
    } finally {
        getActionMetadata().stack.pop();
    }

    // if it is a lazy orchestrator, we want the clone of the action to log it,
    // not the initial action that imports the bundle
    if (datapoint && actionMessage?.lazyOrchestrator) {
        actionMessage.actionDatapoint = datapoint;
        datapoint = null;
    }

    if (datapoint) {
        if (!config.skipAutoEndDatapoint) {
            const dp = datapoint;
            // if the return value is not a promise, then let's create a promise and resolve it right away
            // this will capture all of the javascript in the call stack
            const returnPromise = returnValue?.then ? returnValue : Promise.resolve();
            returnPromise
                .then(() => {
                    dp.setEndPending();
                })
                .catch((error: Error) => {
                    dp.endAction(undefined, error);
                });
        }

        actionDatapointStack.pop();
    }

    return returnValue;
}
